import DataTable, { Column } from '@/components/CommonComponents/DataTable';
import Pagination from '@/components/CommonComponents/Pagination';
import PageHeaderWrapper from '@/components/Layout/PageHeaderWrapper';
import { showToast } from '@/helper/toast.helper';
import { usePermission } from '@/hooks/usePermission';
import { AppDispatch, RootState } from '@/store';
import {
  deleteProjectUser,
  fetchProjectUsers,
  updateProjectUserStatus,
} from '@/store/slices/adminUsersSlice/projectSystemUserSlice';
import { RowPermissions } from '@/types/surveySet.types';
import { ProjectUser } from '@/types/systemprojectuser.types';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createAuthorizer } from '../authguards/casbin/enforcer';
import DeleteModal from '../CommonComponents/DeleteModal';
import ViewDetailsModal from '../CommonComponents/ViewDetailsModal';

const columns: Column<ProjectUser>[] = [
  {
    key: 'projectId',
    header: 'Project ID',
  },
  {
    key: 'project',
    header: 'Project Name',
    render: (_, row) => row.project?.name ?? 'N/A',
  },
  {
    key: 'assignedAt',
    header: 'Assigned Date',
    render: (v) => v?.split('T')[0],
  },
  {
    key: 'status',
    header: 'Project Status',
    render: (_, row) => (row.project?.status ? 'Active' : 'Inactive'),
  },
];

const SystemProjectUsers: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { data, meta, loading } = useSelector((state: RootState) => state.systemProjectUsers);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [tableData, setTableData] = useState<ProjectUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState<ProjectUser | undefined>(undefined);

  useEffect(() => {
    dispatch(
      fetchProjectUsers({
        currentPage,
        pageSize,
      }),
    );
  }, [dispatch, currentPage, pageSize]);

  const handleDelete = async (row: ProjectUser) => {
    setDeleteRow(row);
    setIsDeleteOpen(true);
  };

  const handleDeleteRow = async () => {
    if (!deleteRow) return;
    try {
      await dispatch(deleteProjectUser(deleteRow.id)).unwrap();
      showToast.success('Project user removed successfully');
      setIsDeleteOpen(false);
      setDeleteRow(undefined);
    } catch {
      showToast.error('Failed to delete project user');
    }
  };

  const handleStatusToggle = useCallback(
    async (id: string, newStatus: boolean) => {
      const userId = Number(id);
      const user = data.find((u) => u.id === userId);

      if (!id) {
        console.error('Invalid project user id', id);
        return;
      }

      try {
        await dispatch(
          updateProjectUserStatus({
            id: userId,
            status: newStatus,
          }),
        ).unwrap();

        showToast.success('Status updated successfully');
      } catch {
        showToast.error('Failed to update status');
      }
    },
    [dispatch],
  );

  const permissions = {
    create: usePermission('write', 'users'),
    edit: usePermission('edit', 'users'),
    delete: usePermission('delete', 'users'),
    export: usePermission('export', 'users'),
  };
  const getProjectUserPermissions = async (): Promise<RowPermissions> => {
    const roleInfo = JSON.parse(localStorage.getItem('roleInfo') || 'null');
    const roleName = roleInfo?.name;

    // Admins have full access
    if (roleName === 'ADMIN' || roleName === 'SUPER_ADMIN') {
      return {
        edit: true,
        delete: true,
        preview: true,
        statusToggle: true,
      };
    }

    // Casbin-based permissions
    const authorizer = createAuthorizer(roleName);
    return {
      edit: await authorizer.can('edit', 'project-users'),
      delete: await authorizer.can('delete', 'project-users'),
      preview: await authorizer.can('read', 'project-users'),
      statusToggle: await authorizer.can('edit', 'project-users'),
    };
  };

  useEffect(() => {
    let cancelled = false;

    const buildRows = async () => {
      if (!data || data.length === 0) {
        setTableData([]);
        return;
      }

      const rows: ProjectUser[] = await Promise.all(
        data.map(async (row) => {
          const permissions = await getProjectUserPermissions();

          return {
            ...row,
            _permissions: permissions,
          };
        }),
      );

      if (!cancelled) {
        setTableData(rows);
      }
    };

    buildRows();

    return () => {
      cancelled = true;
    };
  }, [data]);

  const handleView = useCallback((row: ProjectUser) => {
    setSelectedUser({
      id: row.blueNumberId,
      projectName: row.project?.name ?? 'N/A',
      status: row.project?.status ? 'Active' : 'Inactive',
      assignedAt: row.assignedAt?.split('T')[0],
    });

    setIsViewOpen(true);
  }, []);

  useEffect(() => {
    // console.log(
    //   'PROJECT USER ROWS (VIEW DEBUG):',
    //   tableData.map((r) => ({
    //     id: r.id,
    //     preview: r._permissions?.preview,
    //     permissions: r._permissions,
    //   })),
    // );
  }, [tableData]);

  return (
    <PageHeaderWrapper title="Project System Users" description="Manage project users">
      <>
        <DataTable
          listTitle="Project Users"
          columns={columns}
          data={tableData}
          loading={loading}
          onView={handleView}
          onDelete={handleDelete}
          onStatusToggle={handleStatusToggle}
        />

        <ViewDetailsModal
          isOpen={isViewOpen}
          onClose={() => setIsViewOpen(false)}
          title="Project User Details"
          data={selectedUser}
          fields={[
            { label: 'User Name', value: selectedUser?.id, fullWidth: true },
            {
              label: 'Project Name',
              value: selectedUser?.projectName || 'N/A',
              fullWidth: true,
            },
            {
              label: 'ProjectStatus',
              value: selectedUser?.status || 'N/A',
              fullWidth: true,
            },
            {
              label: 'Assigned At',
              value: selectedUser?.assignedAt || 'N/A',
              fullWidth: true,
            },
          ]}
        />
        <DeleteModal
          isOpen={isDeleteOpen}
          onClose={() => {
            setIsDeleteOpen(false);
            setDeleteRow(undefined);
          }}
          onDelete={handleDeleteRow}
          pageTitle="Admin User"
          itemName={`UserName ${deleteRow?.blueNumberId}`}
          assignedProjects={deleteRow?.project ? [deleteRow.project.name] : []}
        />

        <Pagination
          currentPage={currentPage}
          totalPages={meta?.totalPages}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
        />
      </>
    </PageHeaderWrapper>
  );
};

export default SystemProjectUsers;
